// sync values between localStorage and state
import { useState } from 'react'

/**
 * A hook that allows persisting state in localStorage with full TypeScript support
 * @template T The type of the stored value
 * @param {string} key The localStorage key to use
 * @param {T} initialValue The initial value if nothing is stored
 * @returns {[T, (value: T | ((val: T) => T)) => boolean, () => boolean]} A stateful value, a setter that returns success status, and a remover that returns success status
 */
export function useLocalStorage<T> (
  key: string,
  initialValue: T
): [T, (value: T | ((val: T) => T)) => boolean, () => boolean] {
  // State to store our value
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key)
      // Parse stored json or if none return initialValue
      return item ? (JSON.parse(item) as T) : initialValue
    } catch (error) {
      // If error also return initialValue
      console.error('Error reading from localStorage:', error)
      return initialValue
    }
  })

  // Return a wrapped version of useState's setter function that
  // persists the new value to localStorage and returns success status
  const setValue = (value: T | ((val: T) => T)): boolean => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function
          ? value(storedValue)
          : value

      // Save state
      setStoredValue(valueToStore)

      // Save to local storage
      window.localStorage.setItem(key, JSON.stringify(valueToStore))
      return true // Operation succeeded
    } catch (error) {
      console.error('Error writing to localStorage:', error)
      return false // Operation failed
    }
  }

  // Function to remove the value from localStorage
  const removeValue = (): boolean => {
    try {
      window.localStorage.removeItem(key)
      setStoredValue(initialValue)
      return true // Operation succeeded
    } catch (error) {
      console.error('Error removing from localStorage:', error)
      return false // Operation failed
    }
  }

  return [storedValue, setValue, removeValue]
}

export default useLocalStorage


// Usage example:
// const [myVal, setMyVal, removeMyVal] = useLocalStorage<string> ('any_key', 'default_value')
// or simply
// const [myVal, setMyVal] = useLocalStorage<string> ('any_key', 'default_value')
